"use client";

import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { getHomepage } from "@/app/lib/api/public";
import Image from "next/image";
import { SafeImage } from "@/app/components/SafeImage";
import Link from "next/link";
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Star,
  Camera,
  Image as ImageIcon,
  Heart,
  Calendar,
  Clock,
} from "lucide-react";
import { Slide } from "@/app/lib/api/slides";
import { News } from "../lib/api/news";
import { MenuItem } from "../lib/api/menu";
import { Opportunity } from "../lib/api/opportunities";
import { Category } from "@/app/types/homepage";
import { HomePageSkeleton } from "@/app/components/skeletons/page-skeletons";
import { Button } from "@/app/components/ui/button";
import {
  AnimatedTitle,
  AnimatedTitleWithDescription,
} from "@/app/components/ui/animated-title";
import { PartnerLogo } from "../lib/api/partner-logo";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, A11y, Autoplay } from "swiper/modules";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/autoplay";

import "swiper/css";

export default function Home() {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 });
  const [news, setNews] = useState<News[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [partners, setPartners] = useState<PartnerLogo[]>([]);
  const [settings, setSettings] = useState<any>({});
  const [showOrderProcessPopup, setShowOrderProcessPopup] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const homepage = await getHomepage();
        console.log(homepage);
        setSlides(homepage.sliders);
        setNews(homepage.news);
        setCategories(homepage.categories);
        setOpportunities(homepage.opportunities);
        setMenuItems(homepage.middle_menu);
        setPartners(homepage.partners);
        setSettings(homepage.settings);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const popupEnabled = Boolean(settings?.homepage_popup_enabled);
  const popupTitle =
    typeof settings?.homepage_popup_title === "string"
      ? settings.homepage_popup_title.trim()
      : "";
  const popupContent =
    typeof settings?.homepage_popup_content === "string"
      ? settings.homepage_popup_content.trim()
      : "";
  const popupVersionRaw =
    typeof settings?.homepage_popup_version === "string"
      ? settings.homepage_popup_version.trim()
      : String(settings?.homepage_popup_version ?? "").trim();
  const popupVersion = popupVersionRaw || "1";
  const popupStorageKey = `homepage-popup-seen-v${popupVersion}`;

  useEffect(() => {
    if (isLoading) return;
    if (!popupEnabled || !popupContent) {
      setShowOrderProcessPopup(false);
      return;
    }

    try {
      const hasSeenPopup = localStorage.getItem(popupStorageKey) === "1";
      if (!hasSeenPopup) {
        setShowOrderProcessPopup(true);
      }
    } catch {
      setShowOrderProcessPopup(true);
    }
  }, [isLoading, popupEnabled, popupContent, popupStorageKey]);

  useEffect(() => {
    if (emblaApi) {
      const autoplay = setInterval(() => {
        emblaApi.scrollNext();
      }, 5000);

      return () => {
        clearInterval(autoplay);
      };
    }
  }, [emblaApi]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();
  const handleOrderProcessPopupChange = (open: boolean) => {
    setShowOrderProcessPopup(open);
    if (!open) {
      try {
        localStorage.setItem(popupStorageKey, "1");
      } catch {
        /* localStorage erişimi yoksa sessizce devam et */
      }
    }
  };

  if (isLoading) {
    return <HomePageSkeleton />;
  }

  return (
    <div className="min-h-screen">
      {popupEnabled && popupContent && (
        <Dialog
          open={showOrderProcessPopup}
          onOpenChange={handleOrderProcessPopupChange}
        >
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {popupTitle || "Sipariş Süreçleri Hakkında Bilgilendirme"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-gray-700 leading-6">
              {popupContent
                .split(/\n{2,}/)
                .map((block: string) => block.trim())
                .filter(Boolean)
                .map((block: string, idx: number) => (
                  <p key={`popup-paragraph-${idx}`}>{block}</p>
                ))}
            </div>
            <div className="mt-2 flex justify-end">
              <Button onClick={() => handleOrderProcessPopupChange(false)}>
                Anladım
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Hero Slider - Modern Design */}
      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {slides.map((slide) => (
              <div key={slide.id} className="relative flex-[0_0_100%] min-w-0">
                <Link href={slide?.link || ""}>
                  <div className="relative aspect-[21/9] md:aspect-[30/9] w-full">
                    <SafeImage
                      src={slide.image_url}
                      alt={slide.title || "Fotoğraf Albümü"}
                      fill
                      className="object-cover"
                      priority
                    />
                    <div className="absolute inset-0 z-20 flex items-center">
                      <div className="container mx-auto px-6 md:px-12">
                        <div className="max-w-lg">
                          {slide.title && (
                            <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">
                              {slide.title}
                              {slide.yellow_title && (
                                <span className="text-yellow-400">
                                  {" "}
                                  {slide.yellow_title}
                                </span>
                              )}
                            </h1>
                          )}
                          {slide.detail && (
                            <p className="text-lg md:text-xl text-white/90 mb-6">
                              {slide.detail}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Buttons */}
        <button
          onClick={scrollPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all z-20"
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={scrollNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all z-20"
          aria-label="Next slide"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* Key Features Section */}
      <div className="bg-white py-10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center bg-gray-50 p-6 rounded-xl">
              <div className="mr-4 bg-yellow-100 p-3 rounded-full">
                <Camera className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Yüksek Kalite</h3>
                <p className="text-gray-600 text-sm">Premium baskı kalitesi</p>
              </div>
            </div>
            <div className="flex items-center bg-gray-50 p-6 rounded-xl">
              <div className="mr-4 bg-yellow-100 p-3 rounded-full">
                <ImageIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Modern Tasarım</h3>
                <p className="text-gray-600 text-sm">Şık ve zarif modeller</p>
              </div>
            </div>
            <div className="flex items-center bg-gray-50 p-6 rounded-xl">
              <div className="mr-4 bg-yellow-100 p-3 rounded-full">
                <Calendar className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Hızlı Teslimat</h3>
                <p className="text-gray-600 text-sm">7-10 gün içinde teslim</p>
              </div>
            </div>
            <div className="flex items-center bg-gray-50 p-6 rounded-xl">
              <div className="mr-4 bg-yellow-100 p-3 rounded-full">
                <Heart className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Müşteri Memnuniyeti</h3>
                <p className="text-gray-600 text-sm">
                  %100 müşteri odaklı hizmet
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar Layout */}
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-3/4">
              <AnimatedTitleWithDescription
                title="Haberdar Olun!"
                align="left"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {menuItems.slice(0, 6).map((item) => (
                  <Link href={item.url || "#"} key={item.id}>
                    <div className="group relative overflow-hidden rounded-xl shadow-md h-80 transition-all duration-300 hover:shadow-xl">
                      <Image
                        src={item.image_url || "/placeholder.jpg"}
                        alt={item.title || "Album"}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex flex-col justify-end p-6">
                        <h3 className="text-white text-xl font-bold mb-2">
                          {item.title}
                        </h3>
                        <p className="text-white/80 mb-4 text-sm">
                          {item.detail}
                        </p>
                        <div className="bg-white/20 backdrop-blur-sm text-white py-2 px-4 rounded-lg inline-block self-start transition-all group-hover:bg-yellow-400 group-hover:text-black">
                          Keşfet
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Sidebar - Opportunities */}
            {opportunities.length > 0 && (
              <div className="lg:w-1/4 flex flex-col">
                <AnimatedTitle align="left" size="sm">
                  Özel Fırsatlar
                </AnimatedTitle>
                <div className="mt-12 rounded-xl overflow-hidden shadow-lg flex-grow flex flex-col">
                  <div className="relative aspect-[9/16] w-full flex-grow flex">
                    <Image
                      src={opportunities[0]?.image_url || "/placeholder.jpg"}
                      alt={opportunities[0]?.title || "Special Offer"}
                      fill
                      className="object-cover"
                    />
                    <div className="absolute inset-0 flex flex-col justify-end p-6">
                      <h3 className="text-white text-xl font-bold mb-2">
                        {opportunities[0]?.title}
                      </h3>
                      {opportunities[0]?.yellow_title && (
                        <p className="text-yellow-400 mb-3">
                          {opportunities[0].yellow_title}
                        </p>
                      )}
                      {opportunities[0]?.detail && (
                        <p className="text-white/80 text-sm mb-4 line-clamp-3">
                          {opportunities[0].detail}
                        </p>
                      )}
                      <Link href={opportunities[0]?.url || "#"}>
                        <Button className="w-full bg-yellow-400 hover:bg-yellow-500 text-black">
                          Fırsatı Kaçırma
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Text 2 */}
      <div className="container mx-auto py-12 px-4">
        <div className="bg-white rounded-lg p-8 border border-gray-100 relative">
          <div className="absolute top-0 right-0 h-1 w-1/3 bg-orange-500"></div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5 text-gray-800 flex items-center">
            <span className="w-10 h-1 bg-orange-500 mr-3 hidden md:block"></span>
            {settings.text2head}
          </h2>
          <div
            dangerouslySetInnerHTML={{ __html: settings.text2content }}
            className="text-gray-700 text-lg leading-relaxed max-w-4xl"
          />
        </div>
      </div>

      {/* Video */}
      <div className="mx-auto px-4 w-full aspect-[19/9] relative overflow-hidden">
        <video width={"100%"} height={"100%"} autoPlay muted loop playsInline>
          <source src="/images/videos/video.mov" type="video/mp4" />
        </video>
      </div>

      {/* Text 1 */}
      <div className="container mx-auto py-12 px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="bg-white rounded-lg p-8 border border-gray-100">
            <h2 className="text-3xl md:text-4xl font-bold mb-5 text-gray-800 border-b-2 border-orange-500 pb-2 inline-block">
              {settings.text1head}
            </h2>
            <div
              dangerouslySetInnerHTML={{ __html: settings.text1content }}
              className="text-gray-700 text-lg leading-relaxed mt-4"
            />
          </div>

          <div className="bg-white rounded-lg p-6 aspect-[36/11] border-gray-100 relative">
            <SafeImage
              src={settings.text1image ? "http://localhost:8000" + settings.text1image : null}
              alt="Fotoğraf Albümü"
              fill
              className="object-contain"
            />
          </div>
        </div>
      </div>

      {/* Product Showcase */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <AnimatedTitleWithDescription
            title="En Popüler Ürünler"
            description="Sizlere en iyi deneyimi sunabilmek için en kaliteli ürünleri ve baskılarımızı özenle seçiyoruz."
          />

          {/* Categories */}
          {categories.map((category, index) => (
            <div key={category.id} className="mb-16">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-bold">
                  <span className="border-l-4 border-yellow-400 pl-3">
                    {category.category}
                  </span>
                </h3>
                <Link
                  href={"/albumler/" + category.url || "#"}
                  className="text-gray-600 hover:text-yellow-600 transition-colors flex items-center text-sm font-medium"
                >
                  Tümünü Gör <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {category.products.map((product) => (
                  <Link
                    href={`/siparis/albumler/${product.url}-${product.id}`}
                    key={product.id}
                  >
                    <div className="group bg-white rounded-lg overflow-hidden transition-all relative">
                      {category.percentage && (
                        <div className="absolute top-2 right-2 bg-orange-200 text-black text-xs px-2 py-1 rounded-lg z-[3]">
                          Sepette %{category.percentage} indirim!
                        </div>
                      )}
                      <div className="aspect-square relative rounded overflow-hidden">
                        {product.image_url ? (
                          <Image
                            src={product.image_url}
                            alt={product.title}
                            fill
                            className="object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                            <span className="text-gray-400">No image</span>
                          </div>
                        )}
                        {product.price && (
                          <div className="absolute top-3 right-3 bg-white/80 backdrop-blur-sm py-1 px-2 rounded text-sm font-medium">
                            {product.price}₺
                          </div>
                        )}
                      </div>
                      <div className="py-2">
                        <h3 className="font-medium text-gray-800 group-hover:text-yellow-600 transition-colors line-clamp-2">
                          {product.title}
                        </h3>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* News & Updates */}
      {news.length > 0 && (
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <AnimatedTitleWithDescription
              title="Haberler & Duyurular"
              description="En son haberler, etkinlikler ve ürün duyurularından haberdar olun"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {news.slice(0, 3).map((item) => (
                <Link href={"/duyurular/" + item.url || "#"} key={item.id}>
                  <div className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all border border-gray-100">
                    <div className="relative aspect-video overflow-hidden">
                      <Image
                        src={item.image_url || "/placeholder.jpg"}
                        alt={item.title}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center text-xs text-gray-500 mb-3">
                        <Clock className="h-3.5 w-3.5 mr-1" />
                        <span>
                          {new Date(item.date).toLocaleDateString("tr-TR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                      <h3 className="font-semibold text-lg mb-2 group-hover:text-yellow-600 transition-colors line-clamp-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-3">
                        {item.summary}
                      </p>
                      <div className="mt-4 inline-flex items-center text-yellow-600 font-medium">
                        Devamını Oku <ArrowRight className="h-4 w-4 ml-1" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Call to Action */}
      <div className="py-16 bg-gradient-to-r from-gray-900 to-yellow-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Anılarınızı Ölümsüzleştirmek İçin
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Profesyonel fotoğraf albümlerimizle özel anlarınızı en kaliteli
            şekilde saklayın
          </p>
          <Link href="/albumler">
            <Button
              size="lg"
              className="bg-white text-black hover:bg-yellow-400 transition-colors"
            >
              Albümleri Keşfet
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Partners */}
      <div className="container mx-auto px-4">
        <Swiper
          modules={[FreeMode, A11y, Autoplay]}
          spaceBetween={10}
          slidesPerView={"auto"}
          loop={true}
          speed={2500}
          autoplay={{
            delay: 0,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
            reverseDirection: false,
          }}
          freeMode={{
            enabled: true,
            minimumVelocity: 0.02,
            momentum: true,
          }}
        >
          {partners.map((partner, index) => (
            <SwiperSlide
              key={index}
              className="!w-auto !max-w-fit !flex-shrink-0"
            >
              <div className="relative aspect-[2/1] w-full h-[100px]">
                <SafeImage
                  src={partner.image_url}
                  alt={partner.id.toString()}
                  fill
                  className="object-contain"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
